package dev.danko.roblox_craft_ai.client

import dev.danko.roblox_craft_ai.shared.model.PaddleClientConfigDto
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.HTMLScriptElement
import kotlin.coroutines.resume

/**
 * Paddle.js overlay checkout. The page only opens the checkout: plans and credits change when the backend
 * receives Paddle's signed webhook, never from anything the browser reports.
 */
object PaddleCheckout {
    private const val SCRIPT_URL = "https://cdn.paddle.com/paddle/v2/paddle.js"
    private var initializedToken: String? = null
    private var onCompleted: (() -> Unit)? = null

    private suspend fun loadScript(): Boolean {
        if (window.asDynamic().Paddle != null) return true
        return suspendCancellableCoroutine { cont ->
            val script = document.createElement("script") as HTMLScriptElement
            script.src = SCRIPT_URL
            script.async = true
            script.onload = { if (cont.isActive) cont.resume(true) }
            script.onerror = { _, _, _, _, _ -> if (cont.isActive) cont.resume(false); null }
            document.head?.appendChild(script)
        }
    }

    /** Returns false when Paddle.js could not be loaded or checkout is not configured. */
    suspend fun open(
        config: PaddleClientConfigDto,
        priceId: String,
        email: String,
        userId: String,
        locale: String,
        completed: () -> Unit
    ): Boolean {
        val token = config.clientToken
        if (!config.enabled || token.isNullOrBlank() || !loadScript()) return false
        val paddle = window.asDynamic().Paddle ?: return false
        onCompleted = completed
        if (initializedToken != token) {
            if (config.environment == "sandbox") paddle.Environment.set("sandbox")
            val options: dynamic = js("({})")
            options.token = token
            options.eventCallback = { event: dynamic ->
                if (event?.name == "checkout.completed") onCompleted?.invoke()
            }
            paddle.Initialize(options)
            initializedToken = token
        }
        val item: dynamic = js("({})")
        item.priceId = priceId
        item.quantity = 1
        val customer: dynamic = js("({})")
        customer.email = email
        val customData: dynamic = js("({})")
        customData.userId = userId
        val settings: dynamic = js("({})")
        settings.locale = locale
        settings.displayMode = "overlay"
        val request: dynamic = js("({})")
        request.items = arrayOf(item)
        request.customer = customer
        request.customData = customData
        request.settings = settings
        paddle.Checkout.open(request)
        return true
    }
}
